import React, {useCallback, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import computeLSUVs from 'procedures/design/method-config/computeLSUVs';
import {MethodUpdater} from 'procedures/design/ProcedureEditor';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {Method, ProcedureDataParameterSetting} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {transformLsuvsForRowFn} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import ProcedureDataParametersConfigTableAdapter, {
  ProcedureDataParametersConfigTableMetadata,
  ProcedureDataParametersConfigTableRowMetadata,
} from 'procedures/tables/ProcedureDataParametersConfigTableAdapter';
import useRowDelegateController from 'procedures/tables/useRowDelegateController';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import useOnMount from 'ui-library/hooks/useOnMount';

import ProcedureConfigTable from './ProcedureConfigTable';

const baseCreateParametersTableProps: Omit<
  CreateStructuredTableProps<ProcedureDataParametersConfigTableMetadata>,
  'metadata'
> = {
  name: 'Parameters',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
};

interface DataParameterConfigTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  onReadyForEditing: () => void;
  locked: boolean;
  navigateToStep: (stepCid: string) => void;
}

function makeEmptyDataParameterSetting(): ProcedureDataParameterSetting {
  const cid = uuidv4();
  return {
    dataParameterVersion: {
      cid,
      id: null,
      procedureDataParameterId: null,
      name: '',
      description: '',
      dataType: DEFAULT_DATA_PARAMETER_TYPE,
      minimum: null,
      maximum: null,
      confirmationMethod: ProcedureParameterConfirmationMethod.TABLE,
    },
    values: [
      {
        id: null,
        cid: `${cid}-planned-value`,
        value: {
          value: null,
          rawValue: null,
          floatValue: null,
          type: FieldType.FLOAT,
        },
      },
    ],
  };
}

export default function DataParameterConfigTable(props: DataParameterConfigTableProps): JSX.Element {
  const {method, onChangeMethod} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const adapter = ProcedureDataParametersConfigTableAdapter.getAdapter();
  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const createParametersTableProps = useMemo(
    () => ({...baseCreateParametersTableProps, metadata: {navigateToStep: props.navigateToStep}}),
    [props.navigateToStep]
  );

  const rowDelegateController = useRowDelegateController<
    ProcedureDataParameterSetting,
    ProcedureDataParametersConfigTableMetadata,
    ProcedureDataParametersConfigTableRowMetadata,
    DataParameterType
  >({
    dependentColumnProvenanceIds: 'ALL_DELEGATE',
    details: method.dataParameterSettings,
    getDataTypeByIndex: (index) =>
      method.dataParameterSettings[index]?.dataParameterVersion.dataType ?? DEFAULT_DATA_PARAMETER_TYPE,
    createTable: (installDelegatesIntoRow) => {
      const dataParameterSettings =
        method.dataParameterSettings.length > 0
          ? method.dataParameterSettings
          : [makeEmptyDataParameterSetting()];

      const table = adapter.createTableFromDataParameters(
        createParametersTableProps,
        `procedureDataParamTable-${method.cid}`,
        dataParameterSettings,
        procedureValidationErrors,
        method.parameterUsages
      );

      const rowsWithDelegates = table.rows.map((row, index) => installDelegatesIntoRow(table, row, index));

      if (lsuvs.current == null) {
        lsuvs.current = computeLSUVs(
          rowsWithDelegates,
          table.columns,
          dataParameterSettings,
          (p) => p.dataParameterVersion.id,
          transformLsuvsForRowFn(
            ProcedureDataParametersConfigTableAdapter.makeLastSyncedUpstreamValues,
            method.parameterUsages,
            (model) => model.dataParameterVersion.id
          )
        );
      }

      return {
        ...table,
        rows: table.rows.map((row, index) => {
          const dataParam = dataParameterSettings[index];
          const id = dataParam.dataParameterVersion.id;
          return {
            ...rowsWithDelegates[index],
            ...(id && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
            isFinalized: id != null,
          };
        }),
      };
    },
    getDelegateColumnByColumnAndDataType:
      ProcedureDataParametersConfigTableAdapter.getDelegateColumnByColumnAndDataType,
  });

  const onDataParameterSettingsChangeDetails = useCallback(
    (dataParameterSettings: Array<ProcedureDataParameterSetting>) => {
      onChangeMethod(method.cid, (m) => ({...m, dataParameterSettings}));
      // The parent needs to flush before the row delegates can be refreshed
      setTimeout(() => {
        rowDelegateController.onChangeDetails(dataParameterSettings);
      }, 0);
    },
    [method, onChangeMethod, rowDelegateController]
  );

  // This table is ready immediately
  useOnMount(props.onReadyForEditing);

  return (
    <ProcedureConfigTable
      tableAdapter={adapter}
      onChangeDetails={onDataParameterSettingsChangeDetails}
      onChangeErrorState={props.onChangeErrorState}
      rowToDetail={(
        row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>,
        columns: Array<StructuredTableColumn>
      ) => ProcedureDataParametersConfigTableAdapter.rowToDataParameterSetting(columns, row)}
      table={rowDelegateController.table}
      tableCid={`procedureDataParamTable-${method.cid}`}
      locked={props.locked}
      dataTestAttrs={{component: 'DataParameterConfigTable', element: 'root'}}
    />
  );
}
